<template>
  <v-dialog
    v-model="isOpen"
    max-width="500"
    aria-label="Import recipe from photo"
  >
    <v-card>
      <v-toolbar color="surface">
        <v-btn icon="mdi-close" dark @click="close" aria-label="close" />
        <v-toolbar-title>Import Recipe from Photo</v-toolbar-title>
      </v-toolbar>
      <v-card-text>
        <image-input v-model="files" :height="200" />
        <v-alert v-if="errorMessage" type="error" class="mt-2">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close" :disabled="loading">Cancel</v-btn>
        <v-btn
          color="primary"
          @click="importRecipe"
          :loading="loading"
          :disabled="loading || !files.length"
        >
          Import
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
  import { defineComponent, type PropType, ref, watch } from 'vue';
  import type { Recipe, RecipePayload } from '@/service/recipe/types';
  import type RecipeClient from '@/service/recipe/recipeClient';
  import type { ClientInterface } from '@/service/types';
  import ImageInput from '@/components/inputs/image-input/ImageInput.vue';

  export default defineComponent({
    name: 'ImportRecipeFromImage',
    components: { ImageInput },
    props: {
      client: {
        type: Object as PropType<ClientInterface<Recipe, RecipePayload>>,
        required: true,
      },
      open: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['imported', 'close'],
    setup(props, { emit }) {
      const isOpen = ref(props.open);
      const files = ref<File[]>([]);
      const errorMessage = ref('');
      const loading = ref(false);

      watch(
        () => props.open,
        (val) => {
          isOpen.value = val;
          if (val) {
            files.value = [];
            errorMessage.value = '';
          }
        },
      );
      watch(isOpen, (val) => {
        if (!val) emit('close');
      });

      function close() {
        isOpen.value = false;
        files.value = [];
        errorMessage.value = '';
      }

      async function importRecipe() {
        errorMessage.value = '';
        if (!files.value.length) return;

        loading.value = true;
        try {
          const recipe = await (props.client as RecipeClient).importFromImage(
            files.value[0],
          );
          emit('imported', recipe);
          close();
        } catch (err: unknown) {
          errorMessage.value =
            (err instanceof Error ? err.message : undefined) ??
            'Could not extract a recipe from this image. Please try a clearer photo.';
        } finally {
          loading.value = false;
        }
      }

      return {
        isOpen,
        files,
        errorMessage,
        loading,
        close,
        importRecipe,
      };
    },
  });
</script>
